import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { map, Observable, of, catchError } from 'rxjs';
import { ApiService } from 'src/app/_core/_services/api.service';
import { TableColumns } from 'src/app/_share/_models/components/table-columns.model';

import { EntityService } from 'src/app/features/main-application/_services/entity.service';
import { DropdownFilterOptions } from 'primeng/dropdown';

interface JsonFormValidators {
  min?: number;
  max?: number;
  required?: boolean;
  requiredTrue?: boolean;
  email?: boolean;
  minLength?: boolean;
  maxLength?: boolean;
  pattern?: string;
  nullValidator?: boolean;
}
interface JsonFormControlOptions {
  min?: string;
  max?: string;
  step?: string;
  icon?: string;
}
interface JsonFormControls {
  name: string;
  label: string;
  value: string;
  type: string;
  options?: JsonFormControlOptions;
  required: boolean;
  validators: JsonFormValidators;
}

export interface JsonFormData {
  controls: JsonFormControls[];
  columns: TableColumns[];
}

@Component({
  selector: 'app-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormComponent implements OnInit, OnChanges {
  @Input() jsonForm: any;
  @Output() formEvent = new EventEmitter<FormGroup>();
  @Output() myForm: FormGroup = this.fb.group({});
  @Output() myFormArray: FormArray = this.fb.array([]);

  @ViewChild('toc_file') tocFileUpload: any;
  // todo: consider using formarray
  // https://blog.angular-university.io/angular-form-array/
  tab: any;

  selectOptions: any[] = [];
  formData: any;
  selectedOptions: any;
  options: any[] = [];
  multiselectOptions: any[] = [];
  dropdownOptions: { [key: string]: any[] } = {};

  tabEntityMap = {
    vlan: 'Vlans',
    vno: 'Vnos',
    region: 'Regions',
    isp: 'Isps',
    product: 'Products',
    contact: 'Contacts',
    site: 'Sites',
    endcustomer: 'EndCustomers',
    device: 'Devices',
    equipmenttype: 'EquipmentTypes',
    endcustomercircuit: 'EndCustomerCircuits'
  };

  controlIDTypeMap: any = {
    vlan_id: { key: 'vlan', descriptorFields: 'description' },
    vno_id: { key: 'vno', descriptorFields: 'name' },
    region_id: { key: 'region', descriptorFields: 'name' },
    isp_id: { key: 'isp', descriptorFields: 'name' },
    product_id: { key: 'product', descriptorFields: 'name' },
    contact_id: { key: 'contact', descriptorFields: 'name' },
    site_id: { key: 'site', descriptorFields: 'name' },
    end_customer_id: { key: 'endcustomer', descriptorFields: 'name' },
    device_id: { key: 'device', descriptorFields: 'name' },
    equipment_type_id: { key: 'equipmenttype', descriptorFields: 'model' },
    end_customer_circuit_id: {
      key: 'endcustomercircuit',
      descriptorFields: 'circuit_no'
    }
  };

  controlPluralTypeMap: any = {
    // TESTING HERE!!!!
    vnos: { key: 'vno', descriptorFields: 'name' },
    equipment_types: { key: 'equipmenttype', descriptorFields: 'model' }
  };

  uploadedFile: any = null;
  uploadProgress: number = 0;

  filterValue: string = '';

  constructor(
    private fb: FormBuilder,
    public config: DynamicDialogConfig,
    private entityService: EntityService,
    private ref: DynamicDialogRef,
    private api: ApiService,
    private cdr: ChangeDetectorRef
  ) {}

  /**
   * @function
   * @name getValueFromId
   * @description
   * Get the value from the id
   * @param id
   * @returns
   */
  getValueFromId(id: number) {
    return this.selectOptions.find((x) => x.value === id).label;
  }

  close(): void {
    this.ref.close();
  }

  ngOnInit(): void {
    console.log('=== FORM COMPONENT INITIALIZATION ===');
    this.jsonForm = this.config.data.schema;
    this.formData = this.config.data.formdata;
    this.tab = this.config.data.tab;

    console.log('Form schema:', this.jsonForm);
    console.log('Form data:', this.formData);
    console.log('Tab:', this.tab);

    // Debug entity service state
    console.log('=== ENTITY SERVICE STATE CHECK ===');
    console.log('Entity service available:', !!this.entityService);
    console.log('Registered entities:', this.entityService.getRegisteredEntities());
    console.log('Current tab entity config:', this.entityService.getEntityConfig(this.tab?.key));

    this.createForm(this.jsonForm.controls);
    this.populateFormData(this.formData);

    // Check if entities are registered before loading dropdown options
    this.ensureEntitiesRegisteredAndLoadOptions();
  }

  private retryCount = 0;
  private maxRetries = 5;

  private ensureEntitiesRegisteredAndLoadOptions(): void {
    console.log('=== ENSURING ENTITIES REGISTERED ===');
    console.log('Available entities:', this.entityService.getRegisteredEntities());

    // Check if we need to wait for entities to be registered
    const requiredEntities = this.getRequiredEntities();
    console.log('Required entities for this form:', requiredEntities);

    const missingEntities = requiredEntities.filter(
      (entity) => !this.entityService.getRegisteredEntities().includes(entity)
    );

    if (missingEntities.length > 0 && this.retryCount < this.maxRetries) {
      console.log('Missing entities:', missingEntities);
      console.log(
        `Waiting for entities to be registered... (retry ${this.retryCount + 1}/${this.maxRetries})`
      );

      this.retryCount++;

      // Wait a bit and retry
      setTimeout(() => {
        this.ensureEntitiesRegisteredAndLoadOptions();
      }, 1000);
      return;
    }

    // Check if data is loaded for all required entities
    const entitiesWithoutData = requiredEntities.filter((entity) => {
      const data$ = this.entityService.getEntityData$(entity);
      if (!data$) {
        console.log(`No data observable for entity: ${entity}`);
        return true;
      }
      const currentData = data$.getValue();
      console.log(`Entity ${entity} has ${currentData?.length || 0} items loaded`);
      return !currentData || currentData.length === 0;
    });

    if (entitiesWithoutData.length > 0 && this.retryCount < this.maxRetries) {
      console.log('Entities without data:', entitiesWithoutData);
      console.log(
        `Waiting for data to be loaded... (retry ${this.retryCount + 1}/${this.maxRetries})`
      );

      this.retryCount++;

      // Try to load data for missing entities
      const loadPromises = entitiesWithoutData.map((entity) => {
        return new Promise<void>((resolve, reject) => {
          this.entityService.getData(entity).subscribe({
            next: () => resolve(),
            error: (error) => {
              console.error(`Error loading data for ${entity}:`, error);
              reject(error);
            }
          });
        });
      });

      Promise.all(loadPromises)
        .then(() => {
          console.log('All missing entity data loaded successfully');
          this.ensureEntitiesRegisteredAndLoadOptions();
        })
        .catch((error) => {
          console.error('Error loading missing entity data:', error);
          // Continue anyway after a delay
          setTimeout(() => {
            this.ensureEntitiesRegisteredAndLoadOptions();
          }, 1000);
        });
      return;
    }

    if (this.retryCount >= this.maxRetries) {
      console.error('Max retries reached. Loading options anyway...');
    }

    console.log('All required entities are registered and have data, loading dropdown options...');
    this.loadDropdownOptions();
    // Trigger change detection after loading options
    this.cdr.detectChanges();
  }

  private getRequiredEntities(): string[] {
    const entities: string[] = [];

    this.jsonForm?.controls.forEach((control: any) => {
      const dataMapObject = this.getControlMapping(control.name, control.type);
      if (dataMapObject?.key) {
        entities.push(dataMapObject.key);
      }
    });

    return [...new Set(entities)]; // Remove duplicates
  }

  private loadDropdownOptions(): void {
    console.log('Loading dropdown options for controls...');
    this.jsonForm?.controls.forEach((control: any) => {
      console.log('Processing control:', control.name, 'of type:', control.type);
      this.getSelectOptions(control.name, control.type).subscribe(
        (options) => {
          console.log(`Options loaded for ${control.name}:`, options);
          this.dropdownOptions[control.name] = options;

          // If we're in edit mode and have form data, ensure the selected entity is loaded
          if (this.formData && this.formData[control.name]) {
            this.ensureSelectedEntityLoaded(control.name, this.formData[control.name]);
          }

          // Trigger change detection to update the UI
          this.cdr.detectChanges();
        },
        (error) => {
          console.error(`Error loading options for ${control.name}:`, error);
          // Set empty options instead of breaking
          this.dropdownOptions[control.name] = [];
          // Trigger change detection even for empty options
          this.cdr.detectChanges();
        }
      );
    });
  }

  private ensureSelectedEntityLoaded(controlName: string, selectedValue: any): void {
    console.log(`Ensuring selected entity is loaded for ${controlName}:`, selectedValue);

    const dataMapObject = this.controlIDTypeMap[controlName];
    if (!dataMapObject?.key) {
      return;
    }

    const entityId = selectedValue?.id || selectedValue;
    if (!entityId || entityId === 'null' || entityId === 'undefined') {
      return;
    }

    // Check if the entity is already in the dropdown options
    const currentOptions = this.dropdownOptions[controlName] || [];
    const optionExists = currentOptions.some((option: any) => option.value === entityId);

    if (!optionExists) {
      console.log(`Selected entity ${entityId} not found in dropdown options, loading it...`);

      // Load the specific entity by ID
      this.getSelectOptions(controlName, 'select').subscribe(
        (newOptions) => {
          console.log(`Entity ${entityId} loaded, new options:`, newOptions);
          this.dropdownOptions[controlName] = newOptions;
          this.cdr.detectChanges();
        },
        (error) => {
          console.error(`Error loading entity ${entityId}:`, error);
        }
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['jsonForm'].firstChange) {
      this.createForm(changes['jsonForm'].currentValue.controls);
    }
  }

  ngAfterViewInit() {
    // //console.log(this.tocFileUpload.files);
  }

  createFormArray() {
    return this.fb.array([]);
  }

  filter(event: KeyboardEvent, controlName: string, options: DropdownFilterOptions) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.filterValue = filterValue;

    console.log('=== DROPDOWN FILTER DEBUG ===');
    console.log('Filter value:', filterValue);
    console.log('Control name:', controlName);

    // Find the specific control by name
    const control = this.jsonForm?.controls.find((c: any) => c.name === controlName);
    if (control) {
      console.log('Found control:', control.name, 'of type:', control.type);

      this.getSelectOptions(control.name, control.type, this.filterValue).subscribe(
        (newOptions) => {
          console.log('New options received for', controlName, ':', newOptions);
          // Update the dropdown options in our map
          this.dropdownOptions[controlName] = newOptions;
          // Also update the control options for immediate UI update
          control.options = newOptions;
          // Trigger change detection
          this.cdr.detectChanges();
        },
        (error) => {
          console.error('Error fetching options:', error);
          // Set empty options on error
          this.dropdownOptions[controlName] = [];
          control.options = [];
          this.cdr.detectChanges();
        }
      );
    } else {
      console.warn('Control not found for filtering:', controlName);
    }
  }

  onDropdownFilter(event: any, controlName: string) {
    console.log('=== DROPDOWN ONFILTER DEBUG ===');
    console.log('Filter event:', event);
    console.log('Control name:', controlName);

    const filterValue = event.filter;
    console.log('Filter value from event:', filterValue);

    // Use the existing filter logic
    this.getSelectOptions(controlName, 'select', filterValue).subscribe(
      (newOptions) => {
        console.log('New options from onFilter for', controlName, ':', newOptions);
        this.dropdownOptions[controlName] = newOptions;
        this.cdr.detectChanges();
      },
      (error) => {
        console.error('Error in onDropdownFilter:', error);
        this.dropdownOptions[controlName] = [];
        this.cdr.detectChanges();
      }
    );
  }

  createForm(controls: JsonFormControls[]) {
    // takes controls form scheme definition and creates form controls
    //console.log('controls', controls);
    for (const control of controls) {
      const validatorsToAdd = [];
      for (const [key, value] of Object.entries(control.validators)) {
        switch (key) {
          case 'min':
            validatorsToAdd.push(Validators.min(value));
            break;
          case 'max':
            validatorsToAdd.push(Validators.max(value));
            break;
          case 'required':
            if (value) {
              validatorsToAdd.push(Validators.required);
            }
            break;
          case 'requiredTrue':
            if (value) {
              validatorsToAdd.push(Validators.requiredTrue);
            }
            break;
          case 'email':
            if (value) {
              validatorsToAdd.push(Validators.email);
            }
            break;
          case 'minLength':
            validatorsToAdd.push(Validators.minLength(value));
            break;
          case 'maxLength':
            validatorsToAdd.push(Validators.maxLength(value));
            break;
          case 'pattern':
            validatorsToAdd.push(Validators.pattern(value));
            break;
          case 'nullValidator':
            if (value) {
              validatorsToAdd.push(Validators.nullValidator);
            }
            break;
          default:
            break;
        }
      }

      this.myForm.addControl(control.name, this.fb.control(control.value, validatorsToAdd));
    }
  }

  populateFormData(formData: any) {
    console.log('=== POPULATE FORM DATA DEBUG ===');
    console.log('Form data to populate:', formData);

    if (formData != null) {
      for (const controlName of Object.keys(formData)) {
        console.log('Processing control:', controlName, 'with value:', formData[controlName]);

        if (controlName in this.controlPluralTypeMap) {
          console.log('Control is a plural type (multiselect)');
          const dataSelected = this.mapDataToSelectedOptions(formData[controlName]);
          console.log('Mapped selected options:', dataSelected);
          this.myForm.controls[controlName]?.setValue(dataSelected);
        } else if (controlName in this.controlIDTypeMap) {
          console.log('Control is a single select with ID mapping');
          // For single select controls, we need to ensure the related entity is loaded
          const currentValue = formData[controlName];
          const entityId = currentValue?.id || currentValue;

          if (entityId && entityId !== 'null' && entityId !== 'undefined') {
            console.log(`Setting value for ${controlName}:`, entityId);
            this.myForm.controls[controlName]?.setValue(entityId);

            // Ensure the related entity data is loaded for display
            const dataMapObject = this.controlIDTypeMap[controlName];
            if (dataMapObject?.key) {
              console.log(`Ensuring entity ${dataMapObject.key} with ID ${entityId} is loaded`);
              // The entity loading will be handled in getSelectOptions when dropdown options are loaded
            }
          } else {
            console.log(`No valid ID for ${controlName}, setting to null`);
            this.myForm.controls[controlName]?.setValue(null);
          }
        } else {
          console.log('Control is a regular field');
          this.myForm.controls[controlName]?.setValue(formData[controlName]);
        }
      }
    } else {
      console.log('No form data to populate');
    }
  }

  getSelectOptions(
    controlName: string,
    controlType: string,
    filterString?: string
  ): Observable<any[]> {
    console.log('=== GET SELECT OPTIONS ===');
    console.log('getSelectOptions called for:', controlName, controlType, filterString);

    if (!controlName) {
      console.log('No controlName provided, returning empty array');
      return of([]); // Return an empty array if controlName is not provided
    }

    let dataMapObject = this.getControlMapping(controlName, controlType);
    console.log('dataMapObject for', controlName, ':', dataMapObject);

    if (dataMapObject?.key) {
      console.log('Loading data for entity:', dataMapObject.key);
      console.log('Entity service available:', !!this.entityService);
      console.log('Registered entities:', this.entityService.getRegisteredEntities());
      console.log(
        'Entity config for',
        dataMapObject.key,
        ':',
        this.entityService.getEntityConfig(dataMapObject.key)
      );

      // Check if entity is registered before trying to get data
      const entityConfig = this.entityService.getEntityConfig(dataMapObject.key);
      if (!entityConfig) {
        console.log(`Entity ${dataMapObject.key} not registered yet, returning empty array`);
        return of([]);
      }

      // Build query parameters
      const queryParams: any = {};

      // Add search filter if provided
      if (filterString && filterString.trim() !== '') {
        queryParams.q = filterString.trim();
        console.log(`Adding search filter: ${filterString}`);
      }

      // Check if we need to load a specific entity by ID
      if (this.formData && this.formData[controlName]) {
        const currentValue = this.formData[controlName];
        const entityId = Array.isArray(currentValue)
          ? currentValue[0]?.id
          : currentValue?.id || currentValue;

        if (entityId && entityId !== 'null' && entityId !== 'undefined') {
          // Check if the entity is already in the current data
          const currentData$ = this.entityService.getEntityData$(dataMapObject.key);
          const currentData = currentData$ ? currentData$.getValue() : [];
          const entityExists = currentData.some((item: any) => item.id === entityId);

          if (!entityExists) {
            console.log(`Entity ${entityId} not found in current data, loading by ID`);
            queryParams.id = entityId;
          }
        }
      }

      // Return an Observable that will emit the dropdown options
      return this.entityService.getData(dataMapObject.key, queryParams).pipe(
        map((dataList: any) => {
          console.log('=== DATA RECEIVED DEBUG ===');
          console.log('Raw data received for', dataMapObject.key, ':', dataList);
          console.log('Data type:', typeof dataList);
          console.log('Is array:', Array.isArray(dataList));
          console.log('Has data property:', !!dataList?.data);
          console.log('Has body property:', !!dataList?.body);
          console.log('Data property:', dataList?.data);
          console.log('Body property:', dataList?.body);

          // Handle different data structures that might be returned
          let dataArray: any[] = [];

          if (Array.isArray(dataList)) {
            // Direct array
            dataArray = dataList;
            console.log('Using direct array');
          } else if (dataList?.data && Array.isArray(dataList.data)) {
            // Wrapped in data property
            dataArray = dataList.data;
            console.log('Using data.data property');
          } else if (dataList?.body?.data && Array.isArray(dataList.body.data)) {
            // Wrapped in body.data property
            dataArray = dataList.body.data;
            console.log('Using data.body.data property');
          } else if (dataList?.body && Array.isArray(dataList.body)) {
            // Wrapped in body property
            dataArray = dataList.body;
            console.log('Using data.body property');
          } else {
            // Fallback to empty array
            dataArray = [];
            console.log('Using fallback empty array');
          }

          console.log('Extracted data array for', dataMapObject.key, ':', dataArray);
          console.log('Data array length:', dataArray.length);

          if (dataArray.length === 0) {
            console.warn('No data found in array for', dataMapObject.key);
            return [];
          }

          this.dropdownOptions[controlName] = this.mapDataToOptions(dataArray, dataMapObject);
          console.log('Mapped options for', controlName, ':', this.dropdownOptions[controlName]);

          // Check if there's already selected value and set it
          if (this.formData && this.formData[controlName]) {
            // Set the selected value in the form control
            this.setControlValue(controlName, this.formData[controlName]);
          }

          console.log('this.dropdownOptions', this.dropdownOptions);

          return this.dropdownOptions[controlName]; // Return the mapped options
        }),
        catchError((error) => {
          console.error('=== ERROR IN GET SELECT OPTIONS ===');
          console.error('Error for entity', dataMapObject.key, ':', error);
          console.log(
            `Entity ${dataMapObject.key} not registered yet, returning empty array:`,
            error.message
          );
          // Return empty array instead of throwing error, allowing retry mechanism to work
          return of([]);
        })
      );
    } else if (this.isPredefinedOptions(controlName)) {
      // For predefined options, just return them
      this.dropdownOptions[controlName] = this.getPredefinedOptions(controlName);
      console.log(
        'Using predefined options for',
        controlName,
        ':',
        this.dropdownOptions[controlName]
      );
      return of(this.dropdownOptions[controlName]);
    }

    console.log('No options found for', controlName);
    console.log('Available control mappings:', this.controlIDTypeMap);
    console.log('Available plural control mappings:', this.controlPluralTypeMap);
    return of([]); // Return an empty array if no matching options are found
  }

  private setControlValue(controlName: string, value: any) {
    const control = this.myForm.get(controlName);
    if (control) {
      if (Array.isArray(value)) {
        // Ensure that only IDs are stored in the form control
        control.setValue(
          value.map((option) => option?.id ?? option),
          { emitEvent: false }
        );
      } else {
        // If it's a single value, ensure only the ID is stored
        const entityId = value?.id ?? value;
        control.setValue(entityId, { emitEvent: false });

        // If we have a valid ID but no corresponding option in dropdown,
        // we need to ensure the entity is loaded
        if (entityId && entityId !== 'null' && entityId !== 'undefined') {
          const dataMapObject = this.controlIDTypeMap[controlName];
          if (dataMapObject?.key) {
            const currentOptions = this.dropdownOptions[controlName] || [];
            const optionExists = currentOptions.some((option: any) => option.value === entityId);

            if (!optionExists) {
              console.log(
                `Option for ID ${entityId} not found in dropdown, ensuring entity is loaded`
              );
              // The entity loading will be handled in getSelectOptions when the dropdown is opened
            }
          }
        }
      }
    }
  }

  private getControlMapping(controlName: string, controlType: string) {
    if (controlType === 'multiselect') {
      return this.controlPluralTypeMap[controlName] || null;
    }
    if (controlType === 'select' && controlName.includes('_id')) {
      return this.controlIDTypeMap[controlName] || null;
    }
    return null;
  }

  private isPredefinedOptions(controlName: string): boolean {
    return controlName.includes('port_options') || controlName.includes('toc_status');
  }

  private getPredefinedOptions(controlName: string): any[] {
    const control = this.jsonForm.controls.find((ctrl: any) => ctrl.name === controlName);
    return control?.options || [];
  }

  mapDataToOptions(dataList: any, dataMapObject: any): any {
    console.log('=== MAP DATA TO OPTIONS DEBUG ===');
    console.log('Data list:', dataList);
    console.log('Data map object:', dataMapObject);
    console.log('Descriptor fields:', dataMapObject.descriptorFields);

    const mappedOptions = dataList.map((data: any) => {
      console.log('Processing data item:', data);
      console.log('Descriptor field value:', data[dataMapObject.descriptorFields]);
      // Some entities (e.g. end customers) can have their primary descriptor
      // field (e.g. 'name') left blank while still carrying first_name/
      // last_name - fall back to those rather than showing an empty label.
      let label = data[dataMapObject.descriptorFields];
      if (!label && (data.first_name || data.last_name)) {
        label = [data.first_name, data.last_name].filter(Boolean).join(' ');
      }
      const option = { label: label || 'Unnamed', value: data.id };
      console.log('Created option:', option);
      return option;
    });

    console.log('Final mapped options:', mappedOptions);
    return mappedOptions;
  }

  mapDataToSelectedOptions(dataList: any): any {
    return dataList.map((data: any) => {
      return data.id;
    });
  }

  onUpload($event: any) {
    //console.log('start of onUpload');
    // // convert file to base64 encoding
    const file = $event.files[0];
    //console.log('file', file);

    this.uploadedFile = null;
    //console.log('end of onUpload');
    // return [file];
  }

  uploadFile(event$: any) {
    const file = event$.files[0];
    // //console.log('file', file);

    const formData = new FormData();

    formData.append('toc_file', file);

    this.api.postFileAPI('upload-file', formData).subscribe((response: any) => {
      //console.log('response', response);

      const data = response.body;

      // fixme: this is a hack to get the file reference into the form
      // todo: disable the controls
      // fixme: disabling the controls leaves out the value from the form submission
      this.myForm.controls['toc_file_reference']?.setValue(data.uniqueId);
      // this.myForm.controls['toc_file_reference']?.disable();

      this.myForm.controls['original_filename']?.setValue(data.originalFileName);
      // this.myForm.controls['original_filename']?.disable();
    });

    this.uploadedFile = file;
    this.tocFileUpload.files = [];

    // return [file];
  }

  onFormSubmit() {
    console.log('=== FORM SUBMIT DEBUG START ===');
    console.log('Form valid: ', this.myForm.valid);
    console.log('Form values: ', this.myForm.value);
    console.log('Original form data: ', this.formData);
    console.log('Tab key: ', this.tab?.key);
    console.log('Tab object: ', this.tab);

    if (this.myForm.valid) {
      // Check if EntityService has the entity registered
      const entityConfig = this.entityService.getEntityConfig(this.tab.key);
      console.log('Entity config for tab:', entityConfig);

      if (!entityConfig) {
        console.error(`Entity ${this.tab.key} not registered in EntityService`);
        // Show error to user
        if (this.ref) {
          this.ref.close({
            success: false,
            error: `Entity ${this.tab.key} not registered`
          });
        }
        return;
      }

      // todo: possibly introduce entityrelationship calls form here, using the relationship service

      if (this.formData != null) {
        // editing an existing entity
        console.log('=== UPDATE OPERATION ===');
        console.log('Updating entity with ID:', this.formData.id);
        console.log('Update data being sent:', this.myForm.value);
        console.log('Entity service being used:', entityConfig.service);
        console.log('Entity endpoint:', entityConfig.endpoint);

        this.entityService
          .updateEntity(this.tab.key, this.formData.id, this.myForm.value)
          .subscribe({
            next: (response) => {
              console.log('=== UPDATE SUCCESS ===');
              console.log('Update successful:', response);
              if (this.ref) {
                this.ref.close({
                  success: true,
                  action: 'update',
                  data: this.myForm.value
                });
              }
            },
            error: (error) => {
              console.log('=== UPDATE ERROR ===');
              console.error('Update failed:', error);
              console.error('Error details:', {
                message: error.message,
                status: error.status,
                statusText: error.statusText,
                error: error.error
              });
              if (this.ref) {
                this.ref.close({
                  success: false,
                  error: error.message || 'Update failed'
                });
              }
            }
          });
      } else {
        // adding a new entity
        console.log('=== CREATE OPERATION ===');
        console.log('Creating new entity');
        console.log('Create data being sent:', this.myForm.value);

        this.entityService.addEntity(this.tab.key, this.myForm.value).subscribe({
          next: (response) => {
            console.log('=== CREATE SUCCESS ===');
            console.log('Create successful:', response);
            if (this.ref) {
              this.ref.close({
                success: true,
                action: 'create',
                data: this.myForm.value
              });
            }
          },
          error: (error) => {
            console.log('=== CREATE ERROR ===');
            console.error('Create failed:', error);
            console.error('Error details:', {
              message: error.message,
              status: error.status,
              statusText: error.statusText,
              error: error.error
            });
            if (this.ref) {
              this.ref.close({
                success: false,
                error: error.message || 'Create failed'
              });
            }
          }
        });
      }
    } else {
      // Handle form validation errors gracefully
      console.log('=== FORM VALIDATION ERROR ===');
      console.log('Form invalid - showing validation errors');
      console.log('Form errors:', this.myForm.errors);

      // Mark all controls as touched to show validation errors
      this.myForm.markAllAsTouched();

      // Log individual field errors for debugging
      Object.keys(this.myForm.controls).forEach((key) => {
        const control = this.myForm.get(key);
        if (control && control.errors) {
          console.log(`Field ${key} errors:`, control.errors);
        }
      });

      // Don't throw an error, just return without closing the dialog
      // This allows the user to see validation errors and fix them
      return;
    }
    console.log('=== FORM SUBMIT DEBUG END ===');
  }
}
